import React, { useCallback, useContext, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import UpcomingIltItem from "./UpcomingIltItem";
import { VariableSizeList } from "react-window";
import { CircularProgress, Divider } from "@mui/material";
import { Box, ListSubheader, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import TodoItem from "../../TodoList/TodoItem";
import VirtualScrollingContext, {
  VirtualScrollingInfiniteLoader
} from "../../../context/VirtualScrollingContext";
import { sizes } from "../../../theme/theme";
import WindowSizeContext from "../../../context/WindowSizeContext";

const headerSize = 32;
const paddingSize = 32; // vertical padding above and below ilt list item

const rowHeights = {
  loadingSpinner: 48,
  defaultRowHeight: 132
};

const widgetContentPadding = 224; // Vertical space between widget and boundaries of screen

const useStyles = makeStyles(() => ({
  scrollContainer: {
    overscrollBehavior: ({ isLoadingMoreItems }) =>
      isLoadingMoreItems ? "contain" : "initial"
  }
}));

const UpcomingIltList = ({ openInPersonAssignmentModal }) => {
  const {
    windowSize: { height: windowHeight, width: windowWidth }
  } = useContext(WindowSizeContext);

  // Store the size of the ILT item because the height of the item will be dependent on its content
  const itemSizeMap = useRef({});
  const infiniteLoaderRef = useRef(null);
  const { t } = useTranslation();
  const { itemCount, isItemLoaded, items, isNextPageLoading } = useContext(
    VirtualScrollingContext
  );
  const classes = useStyles({ isLoadingMoreItems: isNextPageLoading });

  useEffect(() => {
    if (infiniteLoaderRef.current) {
      // Resets height of rows after items change
      infiniteLoaderRef.current._listRef.resetAfterIndex(0);
    }
  }, [items, windowWidth]);

  const getDateGroupHeader = index => {
    const item = items[index];
    const previousItem = items[index - 1];
    if (index === 0 || item.dateLabel !== previousItem.dateLabel) {
      return t(item.dateLabel);
    }
  };

  const getRowHeight = index => {
    let itemHeight = 0;
    if (!isItemLoaded(index)) {
      itemHeight = rowHeights.loadingSpinner;
    } else {
      itemHeight =
        (itemSizeMap.current[index] || rowHeights.defaultRowHeight) +
        // add the header height if the row contains a date group header
        (!!getDateGroupHeader(index) ? headerSize : 0);
    }
    return itemHeight;
  };

  // This is hacky but it prevents the widget from expanding beyond
  // the vertical height of the screen. We need to calculate this specifically for
  // the ILT widget because a height is required for the FixedSizeList component
  const listSizeHeight = Math.min(
    sizes.widgetMaxHeights.ilt - widgetContentPadding,
    windowHeight - widgetContentPadding,
    items.length * rowHeights.defaultRowHeight
  );

  const handleItemHeightChange = useCallback((index, size) => {
    itemSizeMap.current = {
      ...itemSizeMap.current,
      [index]: size + paddingSize
    };
  }, []);

  // Render an item or a loading indicator.
  const Item = ({ index, style }) => {
    let content;

    if (!isItemLoaded(index)) {
      content = (
        <Box
          display="flex"
          justifyContent="center"
          width="100%"
          height="100%"
          alignItems="center"
        >
          <CircularProgress size={32} />
        </Box>
      );
    } else {
      const dateGroupHeader = getDateGroupHeader(index);
      content = (
        <Box
          display="flex"
          flexDirection="column"
          data-qa="upcoming-ilt-list-item"
        >
          {!dateGroupHeader && <Divider />}
          {dateGroupHeader && (
            <ListSubheader disableSticky>
              <Typography variant="caption" color="textSecondary">
                {dateGroupHeader}
              </Typography>
            </ListSubheader>
          )}
          <TodoItem
            item={
              <UpcomingIltItem
                courseId={items[index].id}
                openInPersonAssignmentModal={openInPersonAssignmentModal}
                onHeightChange={newHeight =>
                  handleItemHeightChange(index, newHeight)
                }
              />
            }
          />
        </Box>
      );
    }

    return <div style={style}>{content}</div>;
  };

  return (
    <VirtualScrollingInfiniteLoader infiniteLoaderRef={infiniteLoaderRef}>
      {({ onItemsRendered, ref }) => (
        <VariableSizeList
          height={listSizeHeight}
          itemCount={itemCount}
          onItemsRendered={onItemsRendered}
          ref={ref}
          itemSize={getRowHeight}
          estimatedItemSize={rowHeights.defaultRowHeight}
          className={classes.scrollContainer}
        >
          {Item}
        </VariableSizeList>
      )}
    </VirtualScrollingInfiniteLoader>
  );
};

UpcomingIltList.propTypes = {
  openInPersonAssignmentModal: PropTypes.func
};

export default UpcomingIltList;
